import React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import strings from "../../core/translate";

// webpack rewrites the image paths leaflet expects, so point them at the bundled files
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Beirut, used only until the garage has coordinates of its own
const FALLBACK_CENTER = [33.8938, 35.5018];

class LocationPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = { query: "", results: [], searching: false, error: "" };
    this.mapNode = React.createRef();
  }

  componentDidMount() {
    const { latitude, longitude } = this.props;
    const hasPoint = this.isValidPoint(latitude, longitude);

    this.map = L.map(this.mapNode.current, {
      center: hasPoint ? [Number(latitude), Number(longitude)] : FALLBACK_CENTER,
      zoom: hasPoint ? 16 : 12,
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(this.map);

    if (hasPoint) {
      this.placeMarker(Number(latitude), Number(longitude), false);
    }

    this.map.on("click", (event) => {
      this.placeMarker(event.latlng.lat, event.latlng.lng, true);
    });

    // the picker is often mounted inside a modal that is still animating
    this.resizeTimer = setTimeout(() => this.map.invalidateSize(), 350);
  }

  componentDidUpdate(prevProps) {
    const { latitude, longitude } = this.props;
    if (
      (prevProps.latitude !== latitude || prevProps.longitude !== longitude) &&
      this.isValidPoint(latitude, longitude)
    ) {
      // coordinates typed into the form fields, keep the marker in sync
      const lat = Number(latitude);
      const lng = Number(longitude);
      const marker = this.marker && this.marker.getLatLng();
      if (!marker || marker.lat !== lat || marker.lng !== lng) {
        this.placeMarker(lat, lng, false);
        this.map.setView([lat, lng], Math.max(this.map.getZoom(), 15));
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  isValidPoint = (lat, lng) =>
    lat !== "" &&
    lat !== null &&
    lat !== undefined &&
    lng !== "" &&
    lng !== null &&
    lng !== undefined &&
    !isNaN(Number(lat)) &&
    !isNaN(Number(lng));

  placeMarker = (lat, lng, notify) => {
    if (!this.map) return;

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = L.marker([lat, lng], {
        draggable: true,
        icon: defaultIcon,
      }).addTo(this.map);
      this.marker.on("dragend", () => {
        const position = this.marker.getLatLng();
        this.emit(position.lat, position.lng);
      });
    }

    if (notify) {
      this.emit(lat, lng);
    }
  };

  emit = (lat, lng) => {
    const latitude = Number(Number(lat).toFixed(6));
    const longitude = Number(Number(lng).toFixed(6));
    if (this.props.onChange) {
      this.props.onChange({ latitude, longitude });
    }
    if (this.props.onAddressResolved) {
      this.reverseGeocode(latitude, longitude);
    }
  };

  reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
      );
      const data = await response.json();
      if (data && data.display_name) {
        this.props.onAddressResolved(data.display_name);
      }
    } catch (err) {
      // an unreachable geocoder must never block picking a point
    }
  };

  handleSearch = async (event) => {
    if (event) event.preventDefault();
    const query = this.state.query.trim();
    if (!query) return;

    this.setState({ searching: true, error: "", results: [] });
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(
          query
        )}`
      );
      const results = await response.json();
      this.setState({
        results: Array.isArray(results) ? results : [],
        error:
          Array.isArray(results) && results.length === 0 ? strings.nodata : "",
      });
    } catch (err) {
      this.setState({ error: strings.error });
    }
    this.setState({ searching: false });
  };

  handleResultClick = (result) => {
    const lat = Number(result.lat);
    const lng = Number(result.lon);
    this.map.setView([lat, lng], 17);
    this.placeMarker(lat, lng, true);
    this.setState({ results: [], query: result.display_name });
  };

  handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.map.setView([latitude, longitude], 17);
        this.placeMarker(latitude, longitude, true);
      },
      () => this.setState({ error: strings.error })
    );
  };

  render() {
    const { query, results, searching, error } = this.state;
    const { height = 320 } = this.props;

    return (
      <div className="location-picker">
        {/* not a <form>: the picker is used inside other forms */}
        <div className="location-picker__search">
          <input
            type="text"
            value={query}
            placeholder={strings.search_location}
            onChange={(e) => this.setState({ query: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                this.handleSearch();
              }
            }}
          />
          <button
            type="button"
            disabled={searching}
            title={strings.search_location}
            onClick={() => this.handleSearch()}
          >
            <i className="nc-icon nc-zoom-split" />
          </button>
          <button
            type="button"
            title={strings.use_my_location}
            onClick={this.handleUseMyLocation}
          >
            <i className="nc-icon nc-pin-3" />
          </button>
        </div>

        {results.length > 0 && (
          <ul className="location-picker__results">
            {results.map((result) => (
              <li key={result.place_id}>
                <button
                  type="button"
                  onClick={() => this.handleResultClick(result)}
                >
                  {result.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <div className="location-picker__error">{error}</div>}

        <div
          className="location-picker__map"
          style={{ height }}
          ref={this.mapNode}
        />
        <small className="location-picker__hint">{strings.map_hint}</small>
      </div>
    );
  }
}

export default LocationPicker;
